import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Notificacao } from '../interfaces/notificacao';

import { environment } from '../../../environments/environment';
import { NotificacaoBlacklistProjection } from '../interfaces/notificacao-blacklist-projection';
import { Observable, of } from 'rxjs';
import { PageResult } from '../interfaces/page-result';

import { JwtAuthService } from './auth/jwt-auth.service';
import { BlackList } from '../interfaces/blacklist';
import { md5 } from '../utils/md5';

/** Interações gravadas em notificacao_log (as duas primeiras são as mesmas do desktop). */
export type NotificacaoTipoLog = 'BTN_VER' | 'BTN_FECHAR' | 'BTN_ACAO';

/**
 * Created by Eldevan Nery Junior on 02/08/2021-09:27:14
 */
@Injectable({
  providedIn: 'root'
})
export class NotificacaoService {
  readonly path = `${environment.apiURL}/notificacao`;
  readonly pathInfokaw = `https://infokaw.com.br/api/notificacao`;

  constructor(private http: HttpClient) {}


  create(): Observable<Notificacao> {
    return this.http.post<Notificacao>(`${ this.path}`, null);
  }

  saveOrUpdate(notificacao: Notificacao): Observable<Notificacao> {
    return this.http.put<Notificacao>(`${ this.path}`, notificacao);
  }

  getNotificacao(id: number): Observable<Notificacao> {
    return this.http.get<Notificacao>(`${ this.path}/${id}`);
  }

  deleteNotificacao(id: number): Observable<any> {
    return this.http.delete<any>(`${ this.path}/${id}`);
  }


  getNotificacaoPage(page?: number, size?: number, order?: string,
    direction?: string, desativado?: boolean, tipoNotificacao?: string): Observable<PageResult<Notificacao>> {
    return this.http.get<PageResult<Notificacao>>(`${ this.path}/page/get/?page=${page}&size=${size}`
      + `&order=${order}&direction=${direction ?? 'ASC'}&desativado=${desativado ?? false}&tipoNotificacao=${tipoNotificacao ?? 'TODOS'}`);
  }

  getNotificacaoPageSearch(page?: number, size?: number,
    search?: string, order?: string, direction?: string, desativado?: boolean, tipoNotificacao?: string):
    Observable<PageResult<Notificacao>> {
    return this.http.get<PageResult<Notificacao>>(`${ this.path}/page/get/?page=${page}&size=${size}&search=${search}`
      + `&order=${order}&direction=${direction ?? 'ASC'}&desativado=${desativado ?? false}&tipoNotificacao=${tipoNotificacao ?? 'TODOS'}`);
  }

  getClientes(id: number): Observable<NotificacaoBlacklistProjection[]> {
    return this.http.get<NotificacaoBlacklistProjection[]>(`${ this.path}/clientes/${id}`);
  }

  addCliente(id: number, codigo: string): Observable<NotificacaoBlacklistProjection[]> {
    return this.http.put<NotificacaoBlacklistProjection[]>(`${ this.path}/clientes/add/${id}?codigo=${codigo}`, null);
  }

  removeCliente(id: number, codigo: string): Observable<NotificacaoBlacklistProjection[]> {
    return this.http.put<NotificacaoBlacklistProjection[]>(`${ this.path}/clientes/remove/${id}?codigo=${codigo}`, null);
  }

  getNotificacoesValidas(filialLoginCodigo: string, page = 0, size = 20): Observable<Notificacao[]> {
    let url = `${this.path}/check/list/valido?page=${page}&size=${size}&filialLoginCodigo=${encodeURIComponent(filialLoginCodigo)}`;
    console.log(`NOTIFICACAOSERVICE - GETNOTIFICACOESVALIDAS - URL: ${url}`);
    return this.http.get<Notificacao[]>(url);
  }

  getNotificacoes(): Observable<Notificacao[]> {
    return this.http.get<Notificacao[]>(`${ this.path}/check/list`);
  }

  getLastNotificacao(): Observable<Notificacao> {
    return this.http.get<Notificacao>(`${ this.path}/check`);
  }


  getNotificacoesInfokaw(): Observable<Notificacao[]> {
    return this.http.get<Notificacao[]>(`${ this.pathInfokaw}/check/list`);
  }

  getNotificacoesValidasInfokaw(filialLoginCodigo: string, page = 0, size = 20): Observable<Notificacao[]> {
    let url = `${this.pathInfokaw}/check/list/valido?page=${page}&size=${size}&filialLoginCodigo=${encodeURIComponent(filialLoginCodigo)}`;
    console.log(`NOTIFICACAOSERVICE - GETNOTIFICACOES_INFOKAW_VALIDAS - URL: ${url}`);
    return this.http.get<Notificacao[]>(url);
  }

  getLastNotificacaoInfokaw(): Observable<Notificacao> {
    return this.http.get<Notificacao>(`${ this.path}/check`);
  }

  getNotificacaoInfokaw(id: number): Observable<Notificacao> {
    return this.http.get<Notificacao>(`${ this.pathInfokaw}/${id}`);
  }

  saveOrUpdateInfokaw(notificacao: Notificacao): Observable<Notificacao> {
    return this.http.put<Notificacao>(`${ this.pathInfokaw}`, notificacao);
  }

  /**
   * Registra a interação no log da notificação, no mesmo endpoint do desktop
   * (pass = md5 do CNPJ do cliente). Vai para a API de onde a notificação veio:
   * a central da Infokaw ou a local. Tolerante a falha — log nunca atrapalha a tela.
   */
  registrarLog(notificacao: Notificacao, tipoLog: NotificacaoTipoLog, codigoCliente: string, usuario: string) {
    const id = notificacao?.id;
    const codigo = (codigoCliente || '').replace(/\D/g, '');
    if (!id || !codigo || (notificacao.tipoNotificacao as any) === 'COMPROMISSO') {
      return;
    }
    const base = notificacao.fromInfokaw ? this.pathInfokaw : this.path;
    const url = `${base}/log/add/${id}?codigo=${codigo}`
      + `&usuario=${encodeURIComponent(usuario || '')}`
      + `&tipoLog=${tipoLog}&pass=${md5(codigo)}`;
    this.http.put(url, null).subscribe({ error: () => {} });
  }


}
